import React, { useState, useEffect } from 'react';
import {
  Modal,
  Header,
  Form,
  Button,
  Input,
  Icon,
  Label,
  Message,
  Grid
} from 'semantic-ui-react';
import './config-modal-modern.css';

const BigDataCorpConfigModal = ({ open, unit, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    enabled: false,
    ownerEmail: '',
    ownerPhone: '',
    emailEnabled: true,
    whatsappEnabled: false,
    alertTypes: {
      arrest_warrant: true,
      sanctions: true,
      pep: false,
      criminal_history: false
    }
  });

  const [isEditingContact, setIsEditingContact] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (unit) {
      const ownerData = {
        email: unit.ownerEmail || unit.owner?.email || 'proprietario@email.com',
        phone: unit.ownerPhone || unit.owner?.phone || '+5511999999999'
      };

      const settings = unit.bigDataCorp?.alertSettings || {};
      
      const savedEmail = settings.unitEmail || settings.ownerEmail || ownerData.email;
      const savedPhone = (settings.whatsappRecipients && settings.whatsappRecipients.length > 0) 
        ? settings.whatsappRecipients[0] 
        : settings.ownerPhone || ownerData.phone;
      
      const newFormData = {
        enabled: unit.bigDataCorp?.enabled || false,
        ownerEmail: savedEmail,
        ownerPhone: savedPhone,
        emailEnabled: settings.enabled ?? true,
        whatsappEnabled: settings.whatsappEnabled || false,
        alertTypes: {
          arrest_warrant: settings.alertTypes?.arrest_warrant ?? true,
          sanctions: settings.alertTypes?.sanctions ?? true,
          pep: settings.alertTypes?.pep ?? false,
          criminal_history: settings.alertTypes?.criminal_history ?? false
        }
      };
      
      setFormData(newFormData);
    }
  }, [unit]);

  const handleSave = () => {
    const newErrors = {};
    
    if (formData.enabled) {
      if (!formData.ownerEmail) {
        newErrors.ownerEmail = 'Email é obrigatório';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
        newErrors.ownerEmail = 'Email inválido';
      }
      
      if (!formData.ownerPhone) {
        newErrors.ownerPhone = 'Telefone é obrigatório';
      }

      if (!formData.emailEnabled && !formData.whatsappEnabled) {
        newErrors.notifications = 'Selecione pelo menos um canal de notificação';
      }

      const alertsSelected = Object.values(formData.alertTypes).some(enabled => enabled);
      if (!alertsSelected) {
        newErrors.alertTypes = 'Selecione pelo menos um tipo de alerta';
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const configData = {
        enabled: formData.enabled,
        email: formData.ownerEmail,
        alertSettings: {
          enabled: formData.emailEnabled,
          ownerEmail: formData.ownerEmail,
          unitEmail: formData.ownerEmail,
          ownerPhone: formData.ownerPhone,
          whatsappEnabled: formData.whatsappEnabled,
          whatsappSender: '', // Campo reservado para o número do Twilio
          whatsappRecipients: formData.ownerPhone ? [formData.ownerPhone] : [],
          alertTypes: formData.alertTypes
        }
      };

      const finalUnitId = unit.unitId || unit._id || unit.id;
      
      if (!finalUnitId) {
        alert('Erro: ID da unidade não encontrado. Feche o modal e tente novamente.');
        return;
      }
      
      onSave(finalUnitId, configData);
      onClose();
    }
  };

  const updateNotificationChannel = (channel, enabled) => {
    setFormData(prev => ({
      ...prev,
      [channel]: enabled
    }));
  };

  const updateAlertType = (type, enabled) => {
    setFormData(prev => ({
      ...prev,
      alertTypes: {
        ...prev.alertTypes,
        [type]: enabled
      }
    }));
  };

  const alertTypeLabels = {
    arrest_warrant: 'Mandado de Prisão',
    sanctions: 'Sanções Legais',
    pep: 'Pessoa Politicamente Exposta',
    criminal_history: 'Histórico Criminal'
  };

  const ToggleSwitch = ({ checked, onChange, color = '#3b82f6', disabled = false }) => (
    <div
      onClick={disabled ? undefined : onChange}
      className={`toggle-switch ${checked ? 'active' : 'inactive'} ${disabled ? 'disabled' : ''}`}
      style={{ '--toggle-color': color }}
    >
      <div className={`toggle-switch-handle ${checked ? 'active' : 'inactive'}`}>
        {checked && (
          <Icon name="check" />
        )}
      </div>
    </div>
  );

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      size="large"
      closeIcon
      className="bigdatacorp-config-modal"
    >
      <div className="modal-header-bigdata">
        <div className="modal-header-icon-bigdata">
          <Icon name="shield" />
        </div>
        
        <div>
          <Header as="h2" className="modal-header-title">
            Configuração BigDataCorp
          </Header>
          <p className="modal-header-subtitle">
            {unit?.number ? `Unidade ${unit.number}` : 'Nova configuração'} • Para locações por temporada
          </p>
        </div>
      </div>
      
      <Modal.Content className="modal-content-bigdata">
        <div className="modal-content-padding">
          <Form>
            <div className="config-card">
              <div className="main-toggle-section">
                <div className="main-toggle-left">
                  <div className="main-toggle-header">
                    <div className={`shield-icon ${formData.enabled ? 'active' : 'inactive'}`}>
                      <Icon name="shield" />
                    </div>
                    <div>
                      <h3 className="main-toggle-title">
                        Monitoramento BigDataCorp
                      </h3>
                      <p className="main-toggle-description">
                        Verificação automática de locatários em formulários de locação
                      </p>
                    </div>
                  </div>
                </div>
                
                <ToggleSwitch
                  checked={formData.enabled}
                  onChange={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
                  color="#22c55e"
                />
              </div>
              <div className={`status-badge ${formData.enabled ? 'active' : 'inactive'}`}>
                <Icon 
                  name={formData.enabled ? 'check circle' : 'circle outline'} 
                />
                <span className={`status-text ${formData.enabled ? 'active' : 'inactive'}`}>
                  {formData.enabled ? 'Sistema Ativo' : 'Sistema Inativo'}
                </span>
              </div>
            </div>
            {formData.enabled && (
              <div className="config-card">
                <div className="section-header">
                  <div className="section-header-left">
                    <div className="section-icon contact">
                      <Icon name="user" />
                    </div>
                    <div>
                      <h3 className="section-title">
                        Dados de Contato
                      </h3>
                      <p className="section-description">
                        Informações para recebimento de alertas
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    basic
                    size="small"
                    onClick={() => setIsEditingContact(!isEditingContact)}
                    className="edit-button"
                  >
                    <Icon name={isEditingContact ? 'lock' : 'edit'} />
                    {isEditingContact ? 'Bloquear' : 'Editar'}
                  </Button>
                </div>
                
                <Grid columns={2} stackable>
                  <Grid.Column>
                    <Form.Field error={!!errors.ownerEmail}>
                      <label className="form-field-label">
                        Email do Proprietário
                      </label>
                      <Input
                        icon="mail"
                        iconPosition="left"
                        placeholder="email@exemplo.com"
                        value={formData.ownerEmail}
                        onChange={(e, { value }) => setFormData(prev => ({ ...prev, ownerEmail: value || '' }))}
                        disabled={!isEditingContact}
                        className={`form-input ${!isEditingContact ? 'disabled' : ''}`}
                      />
                      {errors.ownerEmail && (
                        <Label basic color="red" pointing className="error-label">
                          <Icon name="warning" />
                          {errors.ownerEmail}
                        </Label>
                      )}
                    </Form.Field>
                  </Grid.Column>
                  
                  <Grid.Column>
                    <Form.Field error={!!errors.ownerPhone}>
                      <label className="form-field-label">
                        Telefone do Proprietário
                      </label>
                      <Input
                        icon="phone"
                        iconPosition="left"
                        placeholder="+55 11 99999-9999"
                        value={formData.ownerPhone}
                        onChange={(e, { value }) => setFormData(prev => ({ ...prev, ownerPhone: value || '' }))}
                        disabled={!isEditingContact}
                        className={`form-input ${!isEditingContact ? 'disabled' : ''}`}
                      />
                      {errors.ownerPhone && (
                        <Label basic color="red" pointing className="error-label">
                          <Icon name="warning" />
                          {errors.ownerPhone}
                        </Label>
                      )}
                    </Form.Field>
                  </Grid.Column>
                </Grid>
              </div>
            )}

            {formData.enabled && (
              <div className="config-card">
                <div style={{ marginBottom: '20px' }}>
                  <div className="section-header-left">
                    <div className="section-icon notifications">
                      <Icon name="bell" />
                    </div>
                    <div>
                      <h3 className="section-title">
                        Canais de Notificação
                      </h3>
                      <p className="section-description">
                        Escolha como deseja receber os alertas
                      </p>
                    </div>
                  </div>
                  
                  {errors.notifications && (
                    <Message negative size="small" className="error-message">
                      <Icon name="warning circle" />
                      {errors.notifications}
                    </Message>
                  )}
                </div>
                
                <div className="notification-grid">
                  <div 
                    className={`notification-channel email ${formData.emailEnabled ? 'active' : 'inactive'}`}
                    onClick={() => updateNotificationChannel('emailEnabled', !formData.emailEnabled)}
                  >
                    <div className="notification-channel-header">
                      <div className="notification-channel-left">
                        <div className={`notification-channel-icon email ${formData.emailEnabled ? 'active' : 'inactive'}`}>
                          <Icon name="mail" />
                        </div>
                        
                        <div className="notification-channel-info">
                          <h4 className={`notification-channel-title email ${formData.emailEnabled ? 'active' : 'inactive'}`}>
                            Email
                          </h4>
                          <p className={`notification-channel-subtitle email ${formData.emailEnabled ? 'active' : 'inactive'}`}>
                            Alertas por email
                          </p>
                        </div>
                      </div>
                      
                      <ToggleSwitch
                        checked={formData.emailEnabled}
                        onChange={() => updateNotificationChannel('emailEnabled', !formData.emailEnabled)}
                        color="#3b82f6"
                      />
                    </div>
                    
                    <div className={`channel-status-badge email ${formData.emailEnabled ? 'active' : 'inactive'}`}>
                      <div className={`channel-status-dot email ${formData.emailEnabled ? 'active' : 'inactive'}`} />
                      {formData.emailEnabled ? 'ATIVO' : 'INATIVO'}
                    </div>
                  </div>
                  
                  <div className={`notification-channel whatsapp disabled ${formData.whatsappEnabled ? 'active' : 'inactive'}`}>
                    <div className="notification-channel-header">
                      <div className="notification-channel-left">
                        <div className={`notification-channel-icon whatsapp ${formData.whatsappEnabled ? 'active' : 'inactive'}`}>
                          <span role="img" aria-label="WhatsApp" style={{ color: 'white', fontSize: '14px', lineHeight: 1, fontWeight: 'bold' }}>💬</span>
                        </div>
                        
                        <div className="notification-channel-info">
                          <h4 className={`notification-channel-title whatsapp ${formData.whatsappEnabled ? 'active' : 'inactive'}`}>
                            WhatsApp
                          </h4>
                          <p className={`notification-channel-subtitle whatsapp ${formData.whatsappEnabled ? 'active' : 'inactive'}`}>
                            Mensagens instantâneas
                          </p>
                        </div>
                      </div>
                      
                      <ToggleSwitch
                        checked={formData.whatsappEnabled}
                        onChange={() => {}}
                        color="#22c55e"
                        disabled={true}
                      />
                    </div>

                    <div className="warning-badge">
                      <span role="img" aria-label="Aviso" style={{fontSize: '14px'}}>⚠️</span>
                      Indisponível no momento.
                    </div>

                    <div className={`channel-status-badge whatsapp ${formData.whatsappEnabled ? 'active' : 'inactive'}`}>
                      <div className={`channel-status-dot whatsapp ${formData.whatsappEnabled ? 'active' : 'inactive'}`} />
                      {formData.whatsappEnabled ? 'ATIVO' : 'INATIVO'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.enabled && (
              <div className="config-card">
                <div style={{ marginBottom: '20px' }}>
                  <div className="section-header-left">
                    <div className="section-icon alerts">
                      <Icon name="shield" />
                    </div>
                    <div>
                      <h3 className="section-title">
                        Tipos de Alertas
                      </h3>
                      <p className="section-description">
                        Personalize quais alertas de segurança receber
                      </p>
                    </div>
                  </div>
                  
                  {errors.alertTypes && (
                    <Message negative size="small" className="error-message">
                      <Icon name="warning circle" />
                      {errors.alertTypes}
                    </Message>
                  )}
                </div>
                
                <div className="alert-types-grid">
                  {Object.entries(alertTypeLabels).map(([type, label]) => {
                    const isActive = formData.alertTypes[type];
                    const typeClass = type.replace('_', '-');
                    
                    return (
                      <div 
                        key={type}
                        className={`alert-type-card ${typeClass} ${isActive ? 'active' : 'inactive'}`}
                        onClick={() => updateAlertType(type, !isActive)}
                      >
                        <div className="alert-type-header">
                          <div className="alert-type-left">
                            <div className={`alert-type-icon ${isActive ? 'active' : 'inactive'} ${typeClass}`}>
                              <Icon 
                                name={
                                  type === 'arrest_warrant' ? 'gavel' :
                                  type === 'sanctions' ? 'file alternate' :
                                  type === 'pep' ? 'user' : 'history'
                                } 
                              />
                            </div>
                            
                            <div className="alert-type-info">
                              <h4 className={`alert-type-title ${isActive ? 'active' : 'inactive'} ${typeClass}`}>
                                {label}
                              </h4>
                              <p className={`alert-type-subtitle ${isActive ? 'active' : 'inactive'} ${typeClass}`}>
                                {type === 'arrest_warrant' && 'Mandados ativos'}
                                {type === 'sanctions' && 'Sanções e restrições'}
                                {type === 'pep' && 'Exposição política'}
                                {type === 'criminal_history' && 'Antecedentes criminais'}
                              </p>
                            </div>
                          </div>
                          
                          <ToggleSwitch
                            checked={isActive}
                            onChange={() => updateAlertType(type, !isActive)}
                            color="#22c55e"
                          />
                        </div>
                        <div className={`alert-status-badge ${isActive ? 'active' : 'inactive'} ${typeClass}`}>
                          <div className={`alert-status-dot ${isActive ? 'active' : 'inactive'} ${typeClass}`} />
                          {isActive ? 'ATIVO' : 'INATIVO'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {formData.enabled && (
              <div className="info-section">
                <div className="info-decoration" />
                
                <div className="info-content">
                  <div className="info-header">
                    <div className="info-icon">
                      <Icon name="info circle" />
                    </div>
                    <h4 className="info-title">
                      Como funcionam os alertas
                    </h4>
                  </div>
                  
                  <div className="info-grid">
                    <div className="info-item">
                      <Icon name="shield" />
                      <span><strong>Mandado:</strong> Alertas sobre mandados ativos</span>
                    </div>
                    <div className="info-item">
                      <Icon name="file" />
                      <span><strong>Sanções:</strong> Histórico de restrições legais</span>
                    </div>
                    <div className="info-item">
                      <Icon name="user" />
                      <span><strong>PEP:</strong> Pessoas politicamente expostas</span>
                    </div>
                    <div className="info-item">
                      <Icon name="history" />
                      <span><strong>Histórico:</strong> Antecedentes criminais</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Form>
        </div>
      </Modal.Content>
      
      <div className="modal-footer-bigdata">
        <div className="footer-info">
          <Icon name="lock" />
          Configurações salvas automaticamente
        </div>
        
        <div className="footer-actions">
          <Button 
            onClick={onClose}
            size="medium"
            className="footer-cancel-btn"
          >
            <Icon name="times" />
            Cancelar
          </Button>
          
          <Button 
            primary
            onClick={handleSave}
            size="medium"
            className="footer-save-btn"
          >
            <Icon name="save" />
            Salvar Configurações
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BigDataCorpConfigModal;